import JSONAPIAdapter from '@ember-data/adapter/json-api';
import { InstrumentType } from '@square/dashboard-protos/models/squareup/billing/data';
import { inject as service } from '@ember/service';
import * as Sentry from '@sentry/ember';

// eslint-disable-next-line ember/no-classic-classes
export default JSONAPIAdapter.extend({
  init(args) {
    this._super(args);
    this.useFetch = false;
  },
  namespace: 'api/v1/subscriptions',
  features: service(),
  billing: service('subscriptions/billing'),
  find() {
    throw new Error('find is unsupported for subscription-payment-configuration');
  },
  findAll() {
    throw new Error('findAll is unsupported for subscription-payment-configuration');
  },
  async query(store, type, query) {
    /**
     * Perform a proto rpc request rather than a JSON request when fetching the payment
     * instruments. The API was updated, and transforming the data here is much simpler than
     * changing all of the places that consume the data. Once the feature flag is removed,
     * this could be cleaned up to no longer use a JSONAPIAdapter and to make use of a serializer.
     */
    const response = await this.billing.listAvailablePaymentInstruments();
    const transformedInstruments = this.transformInstruments(response, query.unit_token);
    return {
      entities: [transformedInstruments],
      entity: transformedInstruments
    };
  },
  pathForType() {
    return 'payment-configuration';
  },
  transformInstruments(instruments, unitToken) {
    let defaultInstrument = null;

    // eslint-disable-next-line no-prototype-builtins
    if (instruments.hasOwnProperty('defaultInstrumentIndex')) {
      const instrument = instruments.paymentInstruments[instruments.defaultInstrumentIndex];
      if (!instrument) {
        Sentry.captureException(new Error('Default instrument index out of bounds'));
      } else if (instrument.instrumentType === InstrumentType.CARD) {
        defaultInstrument = this.transformCard(instrument);
      } else if (instrument.instrumentType === InstrumentType.BANK_ACCOUNT) {
        defaultInstrument = this.transformBank(instrument);
      }
    }

    // Map the instruments into cards, excluding the default instrument
    const cards = instruments.paymentInstruments.filter(instrument => instrument.instrumentType === InstrumentType.CARD).reject(instrument => instrument.instrumentToken === defaultInstrument?.token).map(card => this.transformCard(card));
    const banks = instruments.paymentInstruments.filter(instrument => instrument.instrumentType === InstrumentType.BANK_ACCOUNT).reject(instrument => instrument.instrumentToken === defaultInstrument?.token).map(bank => this.transformBank(bank));

    // The old model expects the default instrument to be the first item in the list
    let billingSource = null;
    if (defaultInstrument) {
      if (defaultInstrument.instrumentType === InstrumentType.CARD) {
        cards.unshift(defaultInstrument);
        billingSource = {
          type: 'card',
          card: defaultInstrument
        };
      } else if (defaultInstrument.instrumentType === InstrumentType.BANK_ACCOUNT) {
        banks.unshift(defaultInstrument);
        billingSource = {
          type: 'payout_adjustment',
          bank_account: defaultInstrument
        };
      }
    }
    return {
      id: unitToken,
      billing_sources: billingSource ? [billingSource] : [],
      suggested_sources_v2: {
        cards,
        payout_adjustments: banks
      }
    };
  },
  transformCard(card) {
    return {
      brand: card.cardDetails?.brand,
      last_four: card.cardDetails?.lastFour,
      expiration_month: card.cardDetails?.expirationMonth,
      expiration_year: card.cardDetails?.expirationYear,
      cardholder_name: card.cardDetails?.cardholderName,
      token: card.instrumentToken,
      display: card.display,
      instrumentType: InstrumentType.CARD
    };
  },
  transformBank(bank) {
    return {
      account_number_suffix: bank.bankDetails?.accountNumberSuffix,
      account_type: bank.bankDetails?.accountType,
      bank_name: bank.bankDetails?.bankName,
      token: bank.instrumentToken,
      display: bank.display,
      instrumentType: InstrumentType.BANK_ACCOUNT,
      webToken: bank.bankDetails?.webToken
    };
  }
});